import { useCallback, useEffect, useMemo } from 'react';

import { queryElement, useCallbackWhenDomChanges } from '@cast/utils';

import { useTourV2 } from 'core/tour-v2/TourProvider';

import { BLUR_STYLES } from './_styles';

type Props = {
  skippedItems?: string[];
};

function isItemSelected(item: HTMLElement): boolean {
  return item.getAttribute('aria-selected') === 'true';
}

function querySidebarItems(
  sidebar: 'product-sidebar' | 'secondary-sidebar'
): HTMLElement[] {
  return Array.from(
    queryElement({ testId: sidebar })?.querySelectorAll(
      ':is([data-testid^="sidebar-item-"], [data-testid="sidebar-section-title"]):not(.ignore-blur)'
    ) ?? []
  ).filter((it) => it instanceof HTMLElement);
}

function blurSidebarItems(
  items: HTMLElement[],
  ignored: string[],
  style: string
) {
  const setOfIgnoredItems = new Set(ignored);

  items.forEach((it) => {
    if (
      !isItemSelected(it) &&
      !setOfIgnoredItems.has(it.dataset.testid ?? '')
    ) {
      it.classList.add(style);
    }
  });
}

function unblurSidebarItems(items: HTMLElement[], style: string) {
  items.forEach((it) => {
    it.classList.remove(style);
  });
}

const BlurredSidebarInner = (props: Props) => {
  const blurItems = useCallback(() => {
    blurSidebarItems(
      querySidebarItems('product-sidebar'),
      [],
      BLUR_STYLES.light
    );
    blurSidebarItems(
      querySidebarItems('secondary-sidebar'),
      props.skippedItems ?? [],
      BLUR_STYLES.strong
    );
  }, [props.skippedItems]);

  const sidebarElement = useMemo(() => queryElement({ testId: 'sidebar' }), []);

  useCallbackWhenDomChanges(blurItems, true, sidebarElement);

  useEffect(() => {
    blurItems();

    return () => {
      unblurSidebarItems(
        querySidebarItems('product-sidebar'),
        BLUR_STYLES.light
      );
      unblurSidebarItems(
        querySidebarItems('secondary-sidebar'),
        BLUR_STYLES.strong
      );
    };
  }, [blurItems]);

  return null;
};

export const BlurredSidebar = (props: Props) => {
  const { currentStep } = useTourV2();
  // Force remount when a tour step changes to prevent random stale blur state in sidebar
  return <BlurredSidebarInner key={currentStep?.id} {...props} />;
};
